import React, { useCallback, useEffect, useRef } from "react";
import { getConditionCodeList } from "src/apis/commonCodeApi";
import { defaultSYS3060_R01Filter, SYS3060_R01Filter, useSYS3060_R01Store } from "src/containers/mes/SYS/SYS3060_R01/Store_SYS3060_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useToast } from "src/hooks/useToast";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import {  downloadSettingExcel } from "src/utils/blob/blobUtil";
import {
	deleteMenuAuthority,
	downloadExcel_SYS3060_R01,
	getMenuAuthorityAllCount,
	getMenuAuthorityList,
	upsertMenuAuthority
} from "./Api_SYS3060_R01";
import { AgGridReact } from "ag-grid-react";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { fetchFilterConfig} from "src/utils/grid/GridHandlerContent";
import { defaultSYS3060_R01Dto, SYS3060_R01Dto } from "./Dto_SYS3060_R01";
import {  ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { buildCommonCodeRequirements, SYS3060_R01_ROW_VALIDATE } from "src/utils/commonCode/commonCodeRequirementConfig";
import { useCommonCodeValidate } from "src/utils/commonCode/CommonCodeValidateContext";

interface Props {
	gridRef: React.RefObject<AgGridReact<SysCodeDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<SYS3060_R01Dto> | null>;
	setMessage: (message: string) => void;
	
}

/**
 * 권한 그룹별 메뉴 권한 관리 Content 컴포넌트
 * 액션바(조회/저장/삭제/초기화/엑셀)와 주요 로직 담당
 */
export const Content_SYS3060_R01 = (props: Props) => {

	const { setMessage } = props;

	const {
		SYS3060_R01Filter,
		SYS3060_R01PastFilter, 
		SYS3060_R01List, setSYS3060_R01List,
		selectedSYS3060_R01, setSelectedSYS3060_R01,
		menuAuthorityList, setMenuAuthorityList,
		deletedMenuAuthorityList, addDeletedMenuAuthorityList, removeDeletedMenuAuthorityList,
		selectedMenuAuthorityList, removeSelectedMenuAuthorityList,
		selectedSourceSYS3060_R01, setSelectedSourceSYS3060_R01,
		selectedTargetSYS3060_R01, setSelectedTargetSYS3060_R01,
		setOriginalMenuAuthorityList,
		setAllCount,
		initData,
		detailFetchTrigger, setDetailFetchTrigger,
		setScrollTop,
		setDetailScrollTop,
	} = useSYS3060_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { success, error: showError } = useToast();
	const { yesNoList } = useCommonCodeStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	
	// 권한 정보 로드 (useFetchActionbarAuth 훅 사용)
	useFetchActionbarAuth("SYS3060_R01");

	const prevDetailFilterRef = useRef({
		menuCd: SYS3060_R01Filter.menuCd,
		useYn: SYS3060_R01Filter.useYn,
	});

	// 상세 검색 조건(menuCd, useYn) 변경 시 재조회
	useEffect(() => {
		if (!selectedSYS3060_R01) return;
		const prev = prevDetailFilterRef.current;
		if (prev.menuCd !== SYS3060_R01Filter.menuCd || prev.useYn !== SYS3060_R01Filter.useYn) {
			setDetailScrollTop(null);
			prevDetailFilterRef.current = {
				menuCd: SYS3060_R01Filter.menuCd,
				useYn: SYS3060_R01Filter.useYn,
			};
			fetchMenuAuthorityData(selectedSYS3060_R01, SYS3060_R01Filter.useYn);
		}
	}, [SYS3060_R01Filter.menuCd, SYS3060_R01Filter.useYn, selectedSYS3060_R01, setDetailScrollTop]);

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getMenuAuthorityAllCount(getParams(SYS3060_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [SYS3060_R01Filter, setAllCount]);

	// 엑셀 다운로드 핸들러
	const handleExcelClick = useCallback(async () => {
		if (!selectedSYS3060_R01) {
			showError("권한그룹을 선택해주세요.");
			return;
		}

		setLoadingSpinner(true);
			
			const response = await downloadExcel_SYS3060_R01(
				'',
				getParams(SYS3060_R01Filter, selectedSYS3060_R01),
				SYS3060_R01List.length ?? 0
			);

		downloadSettingExcel({ responseData: response.data.data, fileName: '직무별 메뉴 권한등록-excel.xlsx' });
		setLoadingSpinner(false);
	}, [selectedSYS3060_R01, setLoadingSpinner, SYS3060_R01Filter, SYS3060_R01List.length, showError]);

	const defaultSYS3060_R01DetailRow = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(SYS3060_R01_ROW_VALIDATE, {
				yesNoList,
			}),
		);
		if (!commonResult.valid) { return { ...defaultSYS3060_R01Dto }; }
		return {
			...defaultSYS3060_R01Dto,
			queryAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			insertAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			deleteAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			saveAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			printAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			excelAut: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			inserted: true,
		}
	}, [validateCommonCodeLists, yesNoList]);

	// 통합된 조회 함수
	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);
		setDetailScrollTop(null);
		try {
			const autGroupResponse = await getConditionCodeList('AUTGROUP');
			if (autGroupResponse.data.successFlag) {
				setSYS3060_R01List(autGroupResponse.data.data);
				
				// 첫 번째 권한그룹 자동 선택 (권한그룹이 선택되지 않은 경우에만)
				if (autGroupResponse.data.data.length > 0 && !selectedSYS3060_R01) {
					const firstAutGroupCd = autGroupResponse.data.data[0].minorCd;
					setSelectedSYS3060_R01(firstAutGroupCd);
				}
			}else{
				showWarning(autGroupResponse.data.message || "조회된 데이터가 없습니다.");
			}
			
			ShowSuccessFetch(setMessage, "조회가 완료되었습니다.");
			setTimeout(() => setMessage(""), 5000);
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [setLoadingSpinner, setScrollTop, setDetailScrollTop, setMessage, setSYS3060_R01List, selectedSYS3060_R01, setSelectedSYS3060_R01, showError]);

	const getParams = (filter: SYS3060_R01Filter, selectedAutGroupCd?: string) => {
		return {
			autGroupCd: selectedAutGroupCd || filter.autGroupCd || undefined,  // 선택된 권한그룹 우선
			menuCd: filter.menuCd || undefined,
			useYn: filter.useYn || undefined
		};
	};

	const fetchMenuAuthorityData = useCallback(async (autGroupCd: string, useYn?: string) => {
		setLoadingSpinner(true);
		try {
			
			
			const response = await getMenuAuthorityList(
				'',
				getParams(SYS3060_R01Filter, autGroupCd),
				SYS3060_R01List.length ?? 0
			);
			
			if (response.data.successFlag) {
				const menuAuthorityData = Array.isArray(response.data.data) ? response.data.data : [];
				const processAllCount = await renewalAllCount();
				setMenuAuthorityList(menuAuthorityData);
				setOriginalMenuAuthorityList(menuAuthorityData);
				ShowSuccessFetch(setMessage, `메뉴권한 조회가 완료되었습니다. (총 ${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			} else {
				showError(response.data.message || "메뉴 권한 목록 조회에 실패했습니다.");
			}
			
		} catch (error) {
			console.error("메뉴 권한 데이터 조회 중 오류 발생:", error);
			showError("메뉴 권한 데이터 조회 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [setLoadingSpinner, SYS3060_R01Filter, SYS3060_R01List.length, renewalAllCount, setMenuAuthorityList, setOriginalMenuAuthorityList, setMessage, showError]);

	// 권한 복사 버튼 핸들러
	const handleCopyClick = useCallback(async () => {
		if (!selectedSourceSYS3060_R01 || !selectedTargetSYS3060_R01) {
			showError('원본과 대상 권한그룹을 모두 선택해주세요.');
			return;
		}
		if (selectedSourceSYS3060_R01 === selectedTargetSYS3060_R01) {
			showError('원본과 대상 권한그룹은 같을 수 없습니다.');
			return;
		}

		setLoadingSpinner(true);
		try {
			// 1. 원본 권한 데이터 조회
			const sourceParams = getParams(SYS3060_R01Filter, selectedSourceSYS3060_R01);

			const { isDefaultFilter,  listLength } = fetchFilterConfig(
				SYS3060_R01Filter,
				SYS3060_R01PastFilter,
				defaultSYS3060_R01Filter,
				SYS3060_R01List.length
			);
			
			const sourceResponse = await getMenuAuthorityList(
				isDefaultFilter? '' : 'search',
				sourceParams,
				listLength
			);
			
			if (!sourceResponse.data.successFlag) {
				showError(sourceResponse.data.message || "원본 권한 데이터 조회에 실패했습니다.");
				return;
			}
			
			const sourceData = Array.isArray(sourceResponse.data.data) ? sourceResponse.data.data : [];
			
			if (sourceData.length === 0) {
				showError('복사할 권한 데이터가 없습니다.');
				return;
			}

			// 2. 대상 권한그룹으로 데이터 변환 및 저장 (upsert 사용)
			let successCount = 0;
			for (const item of sourceData) {
				// 권한 정보가 있는 메뉴만 복사 (N이 아닌 경우)
				if (item.queryAut !== 'N' || item.insertAut !== 'N' || item.deleteAut !== 'N' || 
					item.saveAut !== 'N' || item.printAut !== 'N' || item.excelAut !== 'N') {
					const targetData = {
						...item,
						autGroupCd: selectedTargetSYS3060_R01
					};
					
					await upsertMenuAuthority(targetData);
					successCount++;
				}
			}

			success(`${successCount}건의 권한이 복사되었습니다.`);
			
			// 3. 복사 후 대상 권한그룹이 현재 선택된 권한그룹이면 목록 새로고침
			if (selectedSYS3060_R01 === selectedTargetSYS3060_R01) {
				await fetchMenuAuthorityData(selectedTargetSYS3060_R01);
			}
			
		} catch (error: any) {
			console.error("권한 복사 중 오류 발생:", error);
			showError(`권한 복사 중 오류가 발생했습니다: ${error.message || error}`);
		} finally {
			setLoadingSpinner(false);
		}
	}, [selectedSourceSYS3060_R01, selectedTargetSYS3060_R01, setLoadingSpinner, showError, SYS3060_R01Filter, SYS3060_R01PastFilter, SYS3060_R01List.length, success, selectedSYS3060_R01, fetchMenuAuthorityData]);

	// 복사 완료 후 초기화 함수
	const handleCopyComplete = useCallback(() => {
		setSelectedSourceSYS3060_R01("");
		setSelectedTargetSYS3060_R01("");
	}, [setSelectedSourceSYS3060_R01, setSelectedTargetSYS3060_R01]);

	// 초기화 핸들러
	const initContainerData = useCallback(() => {
		initData();
		setMessage("");
	}, [initData, setMessage]);

	// 저장 핸들러
	const handleSaveClick = useCallback(async () => {
		// 메뉴 권한 데이터가 있는지 확인
		if (menuAuthorityList.length === 0) {
			showError("저장할 메뉴 권한 데이터가 없습니다.");
			return;
		}

		if (!selectedSYS3060_R01) {
			showError("권한그룹을 선택해주세요.");
			return;
		}

		setLoadingSpinner(true);
		try {
			// 삭제/수정 분기 처리
			const deletedList = deletedMenuAuthorityList.map(item => 
				menuAuthorityList.find(menu => menu.valueIndex === Number(item))
			);
			const modifiedList = menuAuthorityList.filter(item => 
				item.isModified && !deletedMenuAuthorityList.includes(item.menuCd || '')
			);

			// 삭제 처리
			for (const item of deletedList) {
				if (item?.menuCd) {
					await deleteMenuAuthority(selectedSYS3060_R01, item.menuCd);
				}
			}
			
			// 수정 처리 (upsert 사용)
			for (const item of modifiedList) {
				const updateData = {
					...item,
					autGroupCd: selectedSYS3060_R01
				};
				await upsertMenuAuthority(updateData);
			}

			// 성공 메시지
			const successMessages = [];
			if (deletedList.length > 0) successMessages.push(`${deletedList.length}건 삭제`);
			if (modifiedList.length > 0) successMessages.push(`${modifiedList.length}건 수정`);
			
			if (successMessages.length > 0) {
				success(`${successMessages.join(', ')} 완료되었습니다.`);
			} else {
				showError("저장할 데이터가 없습니다.");
			}
			
			// 저장 후 상태 초기화
			removeDeletedMenuAuthorityList(deletedMenuAuthorityList);
			const updatedMenuAuthorityList = menuAuthorityList.map(item => ({
				...item,
				isModified: false
			}));
			setMenuAuthorityList(updatedMenuAuthorityList);
			
			// 저장 후 목록 새로고침
			await fetchMenuAuthorityData(selectedSYS3060_R01);
			
		} catch (error: any) {
			console.error("저장 중 오류 발생:", error);
			showError(`저장 중 오류가 발생했습니다: ${error.message || error}`);
		} finally {
			setLoadingSpinner(false);
		}
	}, [menuAuthorityList, selectedSYS3060_R01, setLoadingSpinner, showError, deletedMenuAuthorityList, removeDeletedMenuAuthorityList, setMenuAuthorityList, fetchMenuAuthorityData, success]);

	// 삭제 핸들러
	const handleDeleteClick = useCallback(() => {
		if (deletedMenuAuthorityList.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedMenuAuthorityList(deletedMenuAuthorityList);
			removeSelectedMenuAuthorityList();
		} else if (selectedMenuAuthorityList.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedMenuAuthorityList(selectedMenuAuthorityList);
			removeSelectedMenuAuthorityList(selectedMenuAuthorityList);
		} else {
			console.log('선택된 행이 없음');
		}
	}, [selectedMenuAuthorityList, deletedMenuAuthorityList, addDeletedMenuAuthorityList, removeDeletedMenuAuthorityList, removeSelectedMenuAuthorityList]);
	

	// 권한그룹 선택 시 상세 조회 (detailFetchTrigger가 true일 때만)
	useEffect(() => {
		if (!detailFetchTrigger || !selectedSYS3060_R01) return;
		fetchMenuAuthorityData(selectedSYS3060_R01, SYS3060_R01Filter.useYn);
		setDetailFetchTrigger(false);
	}, [SYS3060_R01Filter.useYn, detailFetchTrigger, fetchMenuAuthorityData, selectedSYS3060_R01, setDetailFetchTrigger]);

	return {
		handleExcelClick,
		defaultSYS3060_R01DetailRow,
		fetchData,
		fetchMenuAuthorityData,
		initContainerData,
		handleSaveClick,
		handleDeleteClick,
		handleCopyClick,
		handleCopyComplete,
	};
}; 